/**
 * What a form validator reports: which field is wrong, and why.
 *
 * @module utils/field-problems
 */

export interface FieldProblem<Field extends string> {
  field: Field;
  message: string;
}

/**
 * Index problems by field, keeping the first message for each.
 *
 * @example
 * ```ts
 * problemsByField([{ field: "slug", message: "A slug is required." }]).slug;
 * ```
 */
export function problemsByField<Field extends string>(
  problems: readonly FieldProblem<Field>[],
): Partial<Record<Field, string>> {
  const byField: Partial<Record<Field, string>> = {};
  problems.forEach((problem) => {
    byField[problem.field] ??= problem.message;
  });
  return byField;
}

/**
 * Move focus to the control of the first problem.
 *
 * @param problems - In the order the fields appear on screen.
 * @param idFor - The control id each field renders with.
 */
export function focusFirstProblem<Field extends string>(
  problems: readonly FieldProblem<Field>[],
  idFor: (field: Field) => string,
): void {
  const first = problems[0];
  if (first) {
    document.getElementById(idFor(first.field))?.focus();
  }
}

/** The same index with one field's problem dropped, for when it is edited. */
export function withoutProblem<Field extends string>(
  byField: Partial<Record<Field, string>>,
  field: Field,
): Partial<Record<Field, string>> {
  if (!(field in byField)) {
    return byField;
  }
  return Object.fromEntries(Object.entries(byField).filter(([key]) => key !== field)) as Partial<
    Record<Field, string>
  >;
}
